import { ApolloError } from '@apollo/client';
import { EmployeeRole } from '@/types';
import { useAuth } from '@/contexts/auth-context';
import { useGeneratedSalesPortalEmployeeRoleQuery } from '@/experiences/sales-portal/blocs/documents/sales-portal-employee-role.generated';

export interface UseSalesPortalEmployeeRoleResult {
  email: string | null;
  employeeRole: EmployeeRole | null;
  error?: ApolloError;
  loading: boolean;
  loginEmail: string | null;
  refetch: () => Promise<unknown>;
}

/**
 * Hook to fetch the logged-in user's employee role for a supplier.
 *
 * Sales Portal is rendered outside the UserContextProvider, so it cannot use
 * useAuthenticatedUserContext(). This hook provides a dedicated way to fetch
 * the employee role using AuthContext directly.
 *
 * @param supplierUUID - The UUID of the supplier
 * @returns The employee role, contact email, login email, loading state, query error, and refetch
 */
export const useSalesPortalEmployeeRole = (
  supplierUUID: string,
): UseSalesPortalEmployeeRoleResult => {
  const authenticatedUserUUID = useAuth((s) => s.authenticatedUserUUID);

  const { data, error, loading, refetch } =
    useGeneratedSalesPortalEmployeeRoleQuery({
      notifyOnNetworkStatusChange: true,
      skip: !authenticatedUserUUID || !supplierUUID,
      variables: {
        supplierUUID,
        userUUID: authenticatedUserUUID!,
      },
    });

  const businessesEmployee = data?.businesses_by_pk?.businesses_employees?.[0];

  return {
    email: businessesEmployee?.employee.contact_info.email ?? null,
    employeeRole: (businessesEmployee?.employee_role as EmployeeRole) ?? null,
    error,
    loading,
    loginEmail: businessesEmployee?.employee.contact_info.login_email ?? null,
    refetch: () => refetch(),
  };
};
